import React from "react";

import { AccessTime as AccessTimeIcon } from "@material-ui/icons";
import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Typography,
  makeStyles,
} from "@material-ui/core";

import { Brand } from "@hubcapp/types";
import { formatDistance, getInitials } from "@ds-developer/frontend";
import { getBrandName } from "@hubcapp/utils";
import { parsePostContent } from "@hubcapp/utils";

import { PostProps } from "./types";

const useStyles = (brand: Brand) =>
  makeStyles(theme => ({
    avatar: {
      backgroundColor: theme.palette.brand[getBrandName(brand)],
    },
    clockIcon: {
      fontSize: "14px",
      color: theme.palette.text.primary,
      marginRight: theme.spacing(0.5),
    },
    timeText: {
      display: "flex",
      alignItems: "center",
    },
    cardContent: {
      paddingTop: 0,
    },
    article: {
      "& h1, h2, h3, h4, h5, h6": {
        marginBottom: "0.3em",
      },
      "& p, img, ul, ol": {
        marginBottom: theme.spacing(2),
        maxWidth: "100%",
        "&:last-child": {
          margin: 0,
        },
      },
      "& ul, ol": {
        paddingLeft: theme.spacing(3),
      },
    },
  }));

const Post: React.FC<PostProps> = ({ author, date, content, brand, id }) => {
  const classes = useStyles(brand)();

  return (
    <Card data-test="post-card">
      <CardHeader
        avatar={
          <Avatar classes={{ root: classes.avatar }}>
            {getInitials(author)}
          </Avatar>
        }
        title={
          <Typography variant="h5" data-test="post-author">
            {author}
          </Typography>
        }
        subheader={
          <Typography
            variant="body2"
            color="textSecondary"
            classes={{ root: classes.timeText }}
            data-test="post-date-distance"
          >
            <AccessTimeIcon
              data-test="post-clock-icon"
              classes={{ root: classes.clockIcon }}
            />
            <a id={id}>
              {formatDistance(new Date(date), new Date(Date.now()), {
                addSuffix: true,
              })}
            </a>
          </Typography>
        }
      />
      <CardContent classes={{ root: classes.cardContent }}>
        <Typography component="article" classes={{ root: classes.article }}>
          {parsePostContent(content)}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default Post;
